/**
 * Abo-Auswahl auf der Produktseite verdichten — progressive Erweiterung.
 *
 * Ausgangslage (gemessen am 05.08.2026): Die Box der Shopify-Subscriptions-App
 * ist 309 px hoch und steht zwischen Varianten und Kaufbutton. 76 px davon sind
 * die drei Lieferintervalle — sichtbar, obwohl "Einmaliger Kauf" gewaehlt ist.
 * Der Kunde sieht also die Feinheiten einer Option, die er nicht gewaehlt hat,
 * und der Kaufbutton rutscht dafuer unter die Falz.
 *
 * Warum ueberhaupt JavaScript: Im Markup der App gibt es KEINEN "Im Abo"-Radio.
 * Die Abo-Gruppe hat nur eine Ueberschrift und drei Intervall-Radios — die
 * Abo-Entscheidung IST die Intervall-Entscheidung. Reines CSS koennte die Liste
 * zwar einklappen, aber niemand koennte sie wieder oeffnen. Es fehlt schlicht
 * ein Schaltelement, und das kann CSS nicht erzeugen.
 *
 * Sicherheitsprinzip: Dieses Skript setzt `data-lakis-abo="kompakt"` auf die
 * Karte, und NUR daran haengt die kompakte Darstellung im CSS. Laeuft das
 * Skript nicht — Fehler, geblockt, altes Geraet — bleibt exakt der heutige
 * Zustand stehen: Liste offen, alles bedienbar. Ein Ausfall kostet Platz,
 * niemals einen Kauf.
 *
 * Kein Wert ist hier hart geschrieben. Der Sparpreis wird als TEXT aus der
 * guenstigsten Zeile der App uebernommen, nicht neu formatiert — damit steht
 * dort immer genau das, was die App selbst rechnet, in ihrer Waehrung und
 * ihrem Format.
 */

(() => {
  'use strict';

  const KARTE = '.plan_card';
  const ABO_GRUPPE = '.subscription_group:has(.group_list)';

  /** "€8,54 EUR" -> 8.54. Nur zum Vergleichen; angezeigt wird der Originaltext. */
  const alsZahl = (text) => {
    const treffer = text.replace(/\s/g, '').match(/(\d+)[.,](\d{2})/);
    return treffer ? Number(`${treffer[1]}.${treffer[2]}`) : Infinity;
  };

  /** Guenstigste Intervallzeile finden — die Ersparnis ist das Kaufargument. */
  function guenstigste(liste) {
    let besteZeile = null;
    let bester = Infinity;
    for (const preis of liste.querySelectorAll('.in_widget_price')) {
      const wert = alsZahl(preis.textContent);
      if (wert < bester) { bester = wert; besteZeile = preis; }
    }
    return besteZeile ? besteZeile.textContent.trim() : null;
  }

  function verdichten(karte) {
    if (karte.dataset.lakisAbo) return;

    const gruppe = karte.querySelector(ABO_GRUPPE);
    const liste = gruppe && gruppe.querySelector('.group_list');
    const kopf = gruppe && gruppe.querySelector('.title_and_price_wrapper');
    // Ohne eines dieser drei Teile hat die App ihr Markup geaendert. Dann
    // lieber gar nichts tun als etwas Halbes: ohne das data-Attribut greift
    // das kompakte CSS nicht, und die Box bleibt wie sie war.
    if (!liste || !kopf) return;

    const detail = !!karte.closest('.chocolat-product-detail');
    const preis = guenstigste(liste);
    if (preis && !kopf.querySelector('.lakis-abo__ab')) {
      const ab = document.createElement('span');
      ab.className = 'lakis-abo__ab';
      // "ab" + Originaltext der App. Kein eigenes Rechnen, kein eigenes Format.
      // Die Rabattspanne kommt aus den Intervallzeilen der App selbst.
      let spanne = 0;
      for (const li of liste.querySelectorAll('.group_list_item')) {
        const m = li.textContent.match(/(\d+)\s*%/);
        if (m) spanne = Math.max(spanne, Number(m[1]));
      }
      ab.textContent = `ab ${preis}` + (!detail && spanne ? ` · bis ${spanne} %` : '');
      kopf.appendChild(ab);
    }

    // Der Kopf wird zum Schaltelement. Er ist ein <div>, also brauchen
    // Rolle, Fokus und Tastatur eigene Behandlung — sonst ist die Abo-Option
    // fuer Tastatur- und Screenreader-Nutzer schlicht nicht erreichbar.
    if (detail) {
      const titel = kopf.querySelector('.group_name');
      if (titel) titel.textContent = 'Im Abo';
      if (!liste.id) liste.id = `lakis-abo-${karte.closest('[data-variant-id]')?.dataset.variantId || 'plan'}`;
      kopf.setAttribute('aria-controls', liste.id);
      karte.addEventListener('change', (event) => {
        if (event.target.matches('[data-radio-type="one_time_purchase"]')) setzeZustand(karte, kopf, false);
      });
    }
    kopf.setAttribute('role', 'button');
    kopf.setAttribute('tabindex', '0');

    // Hat der Kunde bereits ein Intervall gewaehlt, bleibt offen — sonst
    // wuerde ihm seine eigene Auswahl unter den Haenden verschwinden.
    const gewaehlt = !!liste.querySelector('input:checked');
    setzeZustand(karte, kopf, gewaehlt);

    const umschalten = () => setzeZustand(karte, kopf, karte.dataset.lakisOffen !== 'ja');
    kopf.addEventListener('click', umschalten);
    kopf.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); umschalten(); }
    });

    // Wer ein Intervall waehlt, hat sich entschieden — offen halten.
    liste.addEventListener('change', () => setzeZustand(karte, kopf, true));

    karte.dataset.lakisAbo = 'kompakt';

    // Hoehe reservieren: Die App ersetzt ihr Widget bei jedem Variantenwechsel.
    // Auf schmalen Viewports kann ein laengerer Preis eine weitere Textzeile
    // erzeugen. Deshalb wird dort bereits beim ersten Rendern die gemessene
    // Maximalhoehe unserer kompakten Zwei-Spalten-Darstellung reserviert. Der
    // Austausch verschiebt so weder Genussclub noch Kaufbutton.
    const behaelter = karte.closest('.shopify-block') || karte.parentElement;
    if (behaelter && !detail) {
      requestAnimationFrame(() => {
        const h = behaelter.offsetHeight;
        const bisher = Number.parseFloat(behaelter.style.minHeight) || 0;
        const mobileReserve = window.matchMedia('(max-width: 767px)').matches ? 180 : 0;
        const reserve = Math.max(h, bisher, mobileReserve);
        if (reserve > 0) behaelter.style.minHeight = `${Math.ceil(reserve)}px`;
      });
    }
  }

  function setzeZustand(karte, kopf, offen) {
    karte.dataset.lakisOffen = offen ? 'ja' : 'nein';
    kopf.setAttribute('aria-expanded', String(offen));
  }

  function durchlauf(wurzel = document) {
    for (const karte of wurzel.querySelectorAll(KARTE)) verdichten(karte);
  }

  // Die App zeichnet ihre Karten bei jedem Variantenwechsel neu. Ohne
  // Beobachter waere die Verdichtung nach dem ersten Klick auf eine andere
  // Groesse wieder weg.
  const start = () => {
    durchlauf();
    const behaelter = document.querySelector('.shopify_subscriptions_app_container');
    if (!behaelter) return;
    new MutationObserver(() => durchlauf()).observe(behaelter.parentElement || behaelter, {
      childList: true, subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
